import { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { 
  Calculator, 
  Calendar,
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  Clock,
  CheckCircle,
  Euro
} from "lucide-react";
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  Legend,
  LineChart,
  Line,
  ComposedChart,
  Area,
  PieChart,
  Pie,
  Cell
} from "recharts";

const formatCurrency = (value) => {
  return new Intl.NumberFormat('it-IT', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0
  }).format(value);
};

const formatCurrencyFull = (value) => {
  return new Intl.NumberFormat('it-IT', {
    style: 'currency',
    currency: 'EUR'
  }).format(value);
};

const COLORS = ['#007AFF', '#FF3B30', '#FF9500', '#34C759', '#AF52DE'];

const Tasse = () => {
  const { getAuthHeaders } = useAuth();
  const [loading, setLoading] = useState(true);
  const [forecast, setForecast] = useState(null);
  const [calendar, setCalendar] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [forecastRes, calendarRes] = await Promise.all([
        axios.get(`${API}/taxes/forecast`, getAuthHeaders()),
        axios.get(`${API}/taxes/calendar`, getAuthHeaders())
      ]);
      setForecast(forecastRes.data);
      setCalendar(calendarRes.data);
    } catch (error) {
      toast.error("Errore nel caricamento dati");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  // Prepare pie chart data for tax composition
  const taxComposition = forecast ? [
    { name: 'IVA', value: forecast.yearly_totals.iva_netta },
    { name: 'IRES', value: forecast.yearly_totals.ires_stimata },
    { name: 'IRAP', value: forecast.yearly_totals.irap_stimata }
  ].filter(t => t.value > 0) : [];

  return (
    <div className="animate-fade-in" data-testid="tasse-page">
      <div className="page-header">
        <h1 className="page-title">Previsione Tasse</h1>
        <p className="page-subtitle">Stima delle imposte da versare mese per mese</p>
      </div>

      {/* Summary Cards */}
      {forecast && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Card className="stat-card card-hover bg-gradient-to-br from-[#007AFF]/5 to-white">
            <CardContent className="pt-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs text-[#86868B] uppercase tracking-wider mb-1">IVA Annuale</p>
                  <p className="text-2xl font-semibold text-[#007AFF]">
                    {formatCurrency(forecast.yearly_totals.iva_netta)}
                  </p>
                  <p className="text-sm text-[#86868B] mt-1">
                    Debito: {formatCurrency(forecast.yearly_totals.iva_debito)}
                  </p>
                </div>
                <div className="w-12 h-12 bg-[#007AFF]/10 rounded-2xl flex items-center justify-center">
                  <Euro className="w-6 h-6 text-[#007AFF]" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="stat-card card-hover bg-gradient-to-br from-[#FF9500]/5 to-white">
            <CardContent className="pt-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs text-[#86868B] uppercase tracking-wider mb-1">IRES Stimata</p>
                  <p className="text-2xl font-semibold text-[#FF9500]">
                    {formatCurrency(forecast.yearly_totals.ires_stimata)}
                  </p>
                  <p className="text-sm text-[#86868B] mt-1">
                    Aliquota: {forecast.tax_rates.ires}%
                  </p>
                </div>
                <div className="w-12 h-12 bg-[#FF9500]/10 rounded-2xl flex items-center justify-center">
                  <Calculator className="w-6 h-6 text-[#FF9500]" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="stat-card card-hover bg-gradient-to-br from-[#AF52DE]/5 to-white">
            <CardContent className="pt-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs text-[#86868B] uppercase tracking-wider mb-1">IRAP Stimata</p>
                  <p className="text-2xl font-semibold text-[#AF52DE]">
                    {formatCurrency(forecast.yearly_totals.irap_stimata)}
                  </p>
                  <p className="text-sm text-[#86868B] mt-1">
                    Aliquota: {forecast.tax_rates.irap}%
                  </p>
                </div>
                <div className="w-12 h-12 bg-[#AF52DE]/10 rounded-2xl flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-[#AF52DE]" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="stat-card card-hover bg-gradient-to-br from-[#FF3B30]/5 to-white">
            <CardContent className="pt-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs text-[#86868B] uppercase tracking-wider mb-1">Totale Tasse</p>
                  <p className="text-2xl font-semibold text-[#FF3B30]">
                    {formatCurrency(forecast.yearly_totals.totale_tasse)}
                  </p>
                  <p className="text-sm text-[#86868B] mt-1">
                    Anno {forecast.year}
                  </p>
                </div>
                <div className="w-12 h-12 bg-[#FF3B30]/10 rounded-2xl flex items-center justify-center">
                  <AlertTriangle className="w-6 h-6 text-[#FF3B30]" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      <Tabs defaultValue="monthly" className="space-y-6">
        <TabsList className="bg-[#F5F5F7] p-1 rounded-xl">
          <TabsTrigger 
            value="monthly" 
            className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm px-6"
            data-testid="monthly-tab"
          >
            <Calculator className="w-4 h-4 mr-2" />
            Previsione Mensile
          </TabsTrigger>
          <TabsTrigger 
            value="calendar"
            className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm px-6"
            data-testid="calendar-tab"
          >
            <Calendar className="w-4 h-4 mr-2" />
            Calendario Scadenze
          </TabsTrigger>
        </TabsList>

        {/* Monthly Forecast Tab */}
        <TabsContent value="monthly">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
            {/* Monthly Chart */}
            <Card className="lg:col-span-2 bg-white rounded-2xl border-0 shadow-[0_2px_12px_rgba(0,0,0,0.04)]">
              <CardHeader>
                <CardTitle className="text-lg font-semibold text-[#1D1D1F]">
                  Andamento IVA Mensile
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-[350px]" data-testid="iva-monthly-chart">
                  {forecast?.monthly_forecast ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <ComposedChart data={forecast.monthly_forecast}>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.05)" />
                        <XAxis dataKey="month" stroke="#86868B" fontSize={12} />
                        <YAxis tickFormatter={(v) => `€${(v/1000).toFixed(0)}k`} stroke="#86868B" fontSize={12} />
                        <Tooltip 
                          formatter={(value, name) => {
                            const labels = { 
                              iva_debito: 'IVA Debito', 
                              iva_credito: 'IVA Credito', 
                              iva_netta: 'IVA da Versare' 
                            };
                            return [formatCurrencyFull(value), labels[name] || name];
                          }}
                          contentStyle={{ background: 'white', border: 'none', borderRadius: '12px', boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }}
                        />
                        <Legend formatter={(v) => ({ iva_debito: 'IVA Debito', iva_credito: 'IVA Credito', iva_netta: 'IVA Netta' }[v] || v)} />
                        <Bar dataKey="iva_debito" fill="#FF3B30" radius={[4, 4, 0, 0]} opacity={0.7} />
                        <Bar dataKey="iva_credito" fill="#34C759" radius={[4, 4, 0, 0]} opacity={0.7} />
                        <Line type="monotone" dataKey="iva_netta" stroke="#007AFF" strokeWidth={3} dot={{ fill: '#007AFF', r: 4 }} />
                      </ComposedChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-full flex items-center justify-center text-[#86868B]">
                      Nessun dato disponibile
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Tax Composition Pie */}
            <Card className="bg-white rounded-2xl border-0 shadow-[0_2px_12px_rgba(0,0,0,0.04)]">
              <CardHeader>
                <CardTitle className="text-lg font-semibold text-[#1D1D1F]">
                  Composizione Tasse
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-[250px]" data-testid="tax-pie-chart">
                  {taxComposition.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={taxComposition}
                          cx="50%"
                          cy="50%"
                          innerRadius={60}
                          outerRadius={80}
                          paddingAngle={5}
                          dataKey="value"
                        >
                          {taxComposition.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(value) => formatCurrency(value)} />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-full flex items-center justify-center text-[#86868B]">
                      Nessun dato
                    </div>
                  )}
                </div>
                <div className="mt-4 space-y-2">
                  {taxComposition.map((item, index) => (
                    <div key={item.name} className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-2">
                        <div 
                          className="w-3 h-3 rounded-full" 
                          style={{ backgroundColor: COLORS[index % COLORS.length] }}
                        />
                        <span className="text-[#1D1D1F]">{item.name}</span>
                      </div>
                      <span className="text-[#86868B] font-medium">{formatCurrency(item.value)}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Quarterly IVA */}
          <Card className="bg-white rounded-2xl border-0 shadow-[0_2px_12px_rgba(0,0,0,0.04)] mb-6">
            <CardHeader>
              <CardTitle className="text-lg font-semibold text-[#1D1D1F]">
                Riepilogo IVA Trimestrale
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                {forecast?.quarterly_iva?.map((q, i) => (
                  <div key={q.quarter} className="p-4 bg-[#F5F5F7] rounded-xl">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-semibold text-[#1D1D1F]">{q.quarter}</span>
                      <span className="text-xs text-[#86868B]">{q.months}</span>
                    </div>
                    <p className="text-xl font-semibold text-[#007AFF]">{formatCurrency(q.iva_netta)}</p>
                    <div className="mt-2 text-xs text-[#86868B]">
                      <p>Debito: {formatCurrency(q.iva_debito)}</p>
                      <p>Credito: {formatCurrency(q.iva_credito)}</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Monthly Table */}
          <Card className="bg-white rounded-2xl border-0 shadow-[0_2px_12px_rgba(0,0,0,0.04)]">
            <CardHeader>
              <CardTitle className="text-lg font-semibold text-[#1D1D1F]">
                Dettaglio Mensile {forecast?.year}
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table data-testid="monthly-tax-table">
                  <TableHeader>
                    <TableRow className="hover:bg-transparent">
                      <TableHead className="h-12 px-4 text-xs font-semibold uppercase tracking-wider text-[#86868B] bg-[#F5F5F7]/50">Mese</TableHead>
                      <TableHead className="h-12 px-4 text-xs font-semibold uppercase tracking-wider text-[#86868B] bg-[#F5F5F7]/50 text-right">Impon. Attivo</TableHead>
                      <TableHead className="h-12 px-4 text-xs font-semibold uppercase tracking-wider text-[#86868B] bg-[#F5F5F7]/50 text-right">IVA Debito</TableHead>
                      <TableHead className="h-12 px-4 text-xs font-semibold uppercase tracking-wider text-[#86868B] bg-[#F5F5F7]/50 text-right">IVA Credito</TableHead>
                      <TableHead className="h-12 px-4 text-xs font-semibold uppercase tracking-wider text-[#86868B] bg-[#F5F5F7]/50 text-right">IVA Netta</TableHead>
                      <TableHead className="h-12 px-4 text-xs font-semibold uppercase tracking-wider text-[#86868B] bg-[#F5F5F7]/50 text-right">IRES Acc.</TableHead>
                      <TableHead className="h-12 px-4 text-xs font-semibold uppercase tracking-wider text-[#86868B] bg-[#F5F5F7]/50 text-right">Totale</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {forecast?.monthly_forecast?.map((row) => (
                      <TableRow 
                        key={row.month} 
                        className={`hover:bg-[#F5F5F7]/30 ${row.is_future ? 'opacity-60' : ''} ${row.month_num === forecast.current_month ? 'bg-[#007AFF]/5' : ''}`}
                      >
                        <TableCell className="px-4 py-3 font-medium text-[#1D1D1F]">
                          <div className="flex items-center gap-2">
                            {row.month}
                            {row.month_num === forecast.current_month && (
                              <Badge className="bg-[#007AFF] text-white text-xs">Attuale</Badge>
                            )}
                            {row.is_future && (
                              <Badge variant="outline" className="text-xs">Previsione</Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="px-4 py-3 text-right text-[#1D1D1F]">{formatCurrency(row.imponibile_attivo)}</TableCell>
                        <TableCell className="px-4 py-3 text-right text-[#FF3B30]">{formatCurrency(row.iva_debito)}</TableCell>
                        <TableCell className="px-4 py-3 text-right text-[#34C759]">{formatCurrency(row.iva_credito)}</TableCell>
                        <TableCell className={`px-4 py-3 text-right font-semibold ${row.iva_netta > 0 ? 'text-[#FF3B30]' : 'text-[#34C759]'}`}>
                          {formatCurrency(row.iva_netta)}
                        </TableCell>
                        <TableCell className="px-4 py-3 text-right text-[#FF9500]">{formatCurrency(row.ires_accantonamento)}</TableCell>
                        <TableCell className="px-4 py-3 text-right font-semibold text-[#1D1D1F]">{formatCurrency(row.totale_tasse_mese)}</TableCell>
                      </TableRow>
                    ))}
                    {/* Totals Row */}
                    <TableRow className="bg-[#F5F5F7]/50 font-semibold">
                      <TableCell className="px-4 py-3 text-[#1D1D1F]">TOTALE ANNO</TableCell>
                      <TableCell className="px-4 py-3 text-right text-[#1D1D1F]">{formatCurrency(forecast?.yearly_totals?.imponibile_attivo || 0)}</TableCell>
                      <TableCell className="px-4 py-3 text-right text-[#FF3B30]">{formatCurrency(forecast?.yearly_totals?.iva_debito || 0)}</TableCell>
                      <TableCell className="px-4 py-3 text-right text-[#34C759]">{formatCurrency(forecast?.yearly_totals?.iva_credito || 0)}</TableCell>
                      <TableCell className="px-4 py-3 text-right text-[#FF3B30]">{formatCurrency(forecast?.yearly_totals?.iva_netta || 0)}</TableCell>
                      <TableCell className="px-4 py-3 text-right text-[#FF9500]">{formatCurrency(forecast?.yearly_totals?.ires_stimata || 0)}</TableCell>
                      <TableCell className="px-4 py-3 text-right text-[#1D1D1F]">{formatCurrency(forecast?.yearly_totals?.totale_tasse || 0)}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Calendar Tab */}
        <TabsContent value="calendar">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {calendar?.calendar?.map((month) => (
              <Card 
                key={month.month} 
                className={`bg-white rounded-2xl border-0 shadow-[0_2px_12px_rgba(0,0,0,0.04)] ${month.is_current ? 'ring-2 ring-[#007AFF]' : ''} ${month.is_past ? 'opacity-60' : ''}`}
              >
                <CardHeader className="pb-2">
                  <CardTitle className="text-base font-semibold text-[#1D1D1F] flex items-center justify-between">
                    {month.month_name}
                    {month.is_current && (
                      <Badge className="bg-[#007AFF] text-white text-xs">Mese Attuale</Badge>
                    )}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {month.deadlines.map((deadline, i) => (
                      <div 
                        key={i} 
                        className={`p-3 rounded-xl ${
                          deadline.importanza === 'critica' ? 'bg-[#FF3B30]/10 border border-[#FF3B30]/20' :
                          deadline.importanza === 'alta' ? 'bg-[#FF9500]/10 border border-[#FF9500]/20' :
                          'bg-[#F5F5F7]'
                        }`}
                      >
                        <div className="flex items-start justify-between">
                          <div>
                            <p className="font-medium text-[#1D1D1F] text-sm">{deadline.tipo}</p>
                            <p className="text-xs text-[#86868B] mt-1">{deadline.descrizione}</p>
                          </div>
                          <div className="flex items-center gap-1 text-xs text-[#86868B]">
                            <Clock className="w-3 h-3" />
                            {deadline.giorno}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Legend */}
          <Card className="bg-white rounded-2xl border-0 shadow-[0_2px_12px_rgba(0,0,0,0.04)] mt-6">
            <CardContent className="pt-6">
              <div className="flex flex-wrap gap-6">
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded bg-[#FF3B30]/10 border border-[#FF3B30]/20" />
                  <span className="text-sm text-[#86868B]">Scadenza Critica</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded bg-[#FF9500]/10 border border-[#FF9500]/20" />
                  <span className="text-sm text-[#86868B]">Scadenza Alta Priorità</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded bg-[#F5F5F7]" />
                  <span className="text-sm text-[#86868B]">Scadenza Ordinaria</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Tasse;
